import { gql } from '@apollo/client';

/**
 * Reporter email confirmation operations.
 *
 * These are written inline instead of the usual `src/graphql/**\/*.gql` + generated
 * documents, because the subgraph SDL committed at `schema.graphql` does not yet carry
 * these mutations - codegen cannot type them. Once the backend schema is published and
 * `schema.graphql` is refreshed, move both operations into `src/graphql/mutations/`,
 * run `yarn apollo:types`, and replace the hand-written types below with the generated
 * ones. Do not hand-edit `schema.graphql` to make this compile.
 */

export interface RequestReporterEmailVerificationData {
    request: {
        /**
         * Seconds to wait before another code can be requested.
         *
         * `sent: false` has two meanings and this is what tells them apart: a positive
         * value is a cooldown or a rate limit still running (never tell the reporter it
         * was sent), zero means the address is already confirmed for this session.
         */
        cooldownSeconds: number;
        /** ISO timestamp; null when no code was issued. */
        expiresAt: string | null;
        sent: boolean;
    };
}

export interface RequestReporterEmailVerificationVariables {
    input: { email: string };
}

export interface ConfirmReporterEmailVerificationData {
    confirm: {
        /**
         * Guesses left against the current code. Zero with `verified: false` means there
         * is nothing left to try - no outstanding code, expired, or the budget is spent -
         * so the only way forward is a new code.
         */
        attemptsRemaining: number;
        verified: boolean;
    };
}

export interface ConfirmReporterEmailVerificationVariables {
    input: { code: string; email: string };
}

export const REQUEST_REPORTER_EMAIL_VERIFICATION = gql`
    mutation RequestReporterEmailVerification(
        $input: RequestReporterEmailVerificationInput!
    ) {
        request: requestReporterEmailVerification(input: $input) {
            sent
            expiresAt
            cooldownSeconds
        }
    }
`;

export const CONFIRM_REPORTER_EMAIL_VERIFICATION = gql`
    mutation ConfirmReporterEmailVerification(
        $input: ConfirmReporterEmailVerificationInput!
    ) {
        confirm: confirmReporterEmailVerification(input: $input) {
            verified
            attemptsRemaining
        }
    }
`;
